import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

const SOURCE_KEY = {
  Direct: 'adminAnalytics.direct',
  Google: 'adminAnalytics.google',
  LinkedIn: 'adminAnalytics.linkedin',
  X: 'adminAnalytics.x',
  External: 'adminAnalytics.external',
};

const DEVICE_KEY = {
  Mobile: 'adminAnalytics.mobile',
  Desktop: 'adminAnalytics.desktop',
  Tablet: 'adminAnalytics.tablet',
  Unknown: 'adminAnalytics.unknown',
};

/**
 * Portal traffic for the Ingestion project, read from the ingestion database only (Ydim does not write here).
 */
const IngestionAnalyticsSection = () => {
  const { t, isRTL } = useLanguage();
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);

  const translateSource = (source) => {
    if (source == null || source === '') return t('adminAnalytics.dash');
    const k = SOURCE_KEY[source];
    return k ? t(k) : source;
  };

  const translateDevice = (device) => {
    if (device == null || device === '') return t('adminAnalytics.dash');
    const k = DEVICE_KEY[device];
    return k ? t(k) : device;
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setUnavailable(false);
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_INGESTION_ANALYTICS), {
          credentials: 'include',
        });
        if (!res.ok) {
          if (!cancel) {
            setUnavailable(true);
            setData(null);
          }
          return;
        }
        const json = await res.json();
        if (cancel) return;
        if (!json || json.available === false) {
          setUnavailable(true);
          setData(null);
        } else {
          setData(json);
          setUnavailable(false);
        }
      } catch {
        if (!cancel) {
          setUnavailable(true);
          setData(null);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div
        className="admin-analytics-block admin-analytics-card-skin admin-analytics--ingestion"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="admin-state admin-state--loading" aria-live="polite">
          <div className="admin-loading-dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
          <p className="admin-state__message">{t('ingestionAnalytics.loading')}</p>
        </div>
      </div>
    );
  }
  if (unavailable) {
    return (
      <div
        className="admin-analytics-block admin-analytics-card-skin admin-analytics--ingestion"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="admin-state admin-state--unavailable" role="status">
          <p className="admin-state__message">{t('ingestionAnalytics.unavailable')}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="admin-analytics-block admin-analytics-card-skin admin-analytics--ingestion"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="admin-state admin-state--unavailable" role="status">
          <p className="admin-state__message">{t('ingestionAnalytics.unavailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-analytics admin-analytics-block admin-analytics--ingestion" dir={isRTL ? 'rtl' : 'ltr'}>
      <header className="admin-analytics-hero">
        <h2 className="admin-analytics-title" id="admin-ingestion-analytics-title">
          {t('ingestionAnalytics.title')}
        </h2>
        <p className="admin-analytics-subtitle">{t('ingestionAnalytics.sectionDesc')}</p>
      </header>
      <div className="admin-summary admin-summary--metrics">
        <div className="summary-card">
          <div className="summary-label">{t('ingestionAnalytics.totalVisits')}</div>
          <div className="summary-value">{data.totalVisits ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('ingestionAnalytics.todayVisitors')}</div>
          <div className="summary-value">{data.todayVisitors ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('ingestionAnalytics.pageViews')}</div>
          <div className="summary-value">{data.pageViews ?? 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">{t('ingestionAnalytics.pageRefreshes')}</div>
          <div className="summary-value">{data.pageRefreshes ?? 0}</div>
        </div>
      </div>
      <div className="admin-analytics-tables">
        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('ingestionAnalytics.topPages')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('ingestionAnalytics.page')}</th>
                  <th>{t('ingestionAnalytics.views')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.topPages || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('ingestionAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.topPages || []).map((p) => (
                    <tr key={p.page}>
                      <td>{p.label || p.page}</td>
                      <td>{p.views}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('ingestionAnalytics.devices')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('ingestionAnalytics.device')}</th>
                  <th>{t('ingestionAnalytics.count')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.devices || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('ingestionAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.devices || []).map((d) => (
                    <tr key={d.device}>
                      <td>{translateDevice(d.device)}</td>
                      <td>{d.c}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        <section className="admin-analytics-panel admin-table-card">
          <h3>{t('ingestionAnalytics.sources')}</h3>
          <div className="interactions-table-container">
            <table className="interactions-table">
              <thead>
                <tr>
                  <th>{t('ingestionAnalytics.source')}</th>
                  <th>{t('ingestionAnalytics.count')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.sources || []).length === 0 ? (
                  <tr>
                    <td colSpan="2" className="no-data">
                      {t('ingestionAnalytics.noData')}
                    </td>
                  </tr>
                ) : (
                  (data.sources || []).map((s) => (
                    <tr key={s.source}>
                      <td>{translateSource(s.source)}</td>
                      <td>{s.c}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IngestionAnalyticsSection;
